import { useEffect, useState, useRef, useMemo } from 'react';
import { systemEmailTemplatesAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Mail, Edit, Eye, RotateCcw, Save, FileText, User, Ticket, Shield,
  CreditCard, Gift, AlertCircle, Check, Copy, Calendar, Bell, UserCheck, XCircle, Clock, Code,
  Bold, Italic, Underline, Link as LinkIcon, Image, List, AlignLeft
} from 'lucide-react';

const templateTypeInfo = {
  portal_invite: { 
    label: 'Portal Invitation', 
    icon: User, 
    color: 'bg-blue-100 text-blue-700',
    desc: 'Sent when inviting a customer to the portal'
  },
  portal_password_reset: { 
    label: 'Password Reset', 
    icon: Shield, 
    color: 'bg-amber-100 text-amber-700',
    desc: 'Sent when customer requests password reset'
  },
  ticket_created: { 
    label: 'Ticket Created', 
    icon: Ticket, 
    color: 'bg-purple-100 text-purple-700',
    desc: 'Confirmation when a ticket is created'
  },
  ticket_resolved: { 
    label: 'Ticket Resolved', 
    icon: Check, 
    color: 'bg-emerald-100 text-emerald-700',
    desc: 'Notification when a ticket is resolved'
  },
  warranty_expiring: { 
    label: 'Warranty Expiring', 
    icon: AlertCircle, 
    color: 'bg-red-100 text-red-700',
    desc: 'Reminder before warranty expires'
  },
  payment_received: { 
    label: 'Payment Received', 
    icon: CreditCard, 
    color: 'bg-green-100 text-green-700',
    desc: 'Confirmation when payment is received'
  },
  welcome: { 
    label: 'Welcome Email', 
    icon: Gift, 
    color: 'bg-pink-100 text-pink-700',
    desc: 'Welcome message for new contacts'
  },
  meeting_confirmation: {
    label: 'Meeting Confirmation',
    icon: Calendar,
    color: 'bg-indigo-100 text-indigo-700',
    desc: 'Sent when a meeting is booked'
  },
  meeting_reminder: {
    label: 'Meeting Reminder',
    icon: Bell,
    color: 'bg-orange-100 text-orange-700',
    desc: 'Reminder sent before meeting',
    hasTimer: true
  },
  meeting_agent_notification: {
    label: 'New Booking (Agent)',
    icon: UserCheck,
    color: 'bg-cyan-100 text-cyan-700',
    desc: 'Notifies agent of new booking'
  },
  meeting_cancelled: {
    label: 'Meeting Cancelled',
    icon: XCircle,
    color: 'bg-rose-100 text-rose-700',
    desc: 'Sent when meeting is cancelled'
  },
  custom: { 
    label: 'Custom Template', 
    icon: FileText, 
    color: 'bg-slate-100 text-slate-700',
    desc: 'Custom email template'
  },
};

// Simple HTML formatting helper
const execCommand = (command, value = null) => {
  document.execCommand(command, false, value);
};

export const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', subject: '', body: '', is_active: true, send_before_minutes: null });
  const [saving, setSaving] = useState(false);
  const [useRichEditor, setUseRichEditor] = useState(true);
  const editorRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await systemEmailTemplatesAPI.getAll();
      setTemplates(res.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (template) => {
    setSelectedTemplate(template);
    // Detect if body contains HTML tags
    const hasHtml = /<[^>]+>/.test(template.body);
    setUseRichEditor(hasHtml || true);
    setEditForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      is_active: template.is_active,
      send_before_minutes: template.send_before_minutes
    });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editForm.subject || !editForm.body) {
      toast.error('Subject and body are required');
      return;
    }
    
    setSaving(true);
    try {
      await systemEmailTemplatesAPI.update(selectedTemplate.id, editForm);
      toast.success('Template saved');
      setEditDialog(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (template) => {
    if (!window.confirm('Reset this template to default? Your customizations will be lost.')) return;
    
    try {
      await systemEmailTemplatesAPI.reset(template.id);
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to reset template');
    }
  };

  const handlePreview = async (template) => {
    try {
      const res = await systemEmailTemplatesAPI.preview(template.id);
      setPreviewContent(res.data);
      setPreviewDialog(true);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const insertVariable = (variable) => {
    const varText = `{{${variable}}}`;
    
    if (useRichEditor && editorRef.current) {
      // For contenteditable div
      editorRef.current.focus();
      document.execCommand('insertText', false, varText);
      // Update form state
      setEditForm(prev => ({
        ...prev,
        body: editorRef.current.innerHTML
      }));
      toast.success(`Added ${varText}`);
    } else {
      setEditForm(prev => ({
        ...prev,
        body: prev.body + varText
      }));
      toast.success(`Added ${varText}`);
    }
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      setEditForm(prev => ({
        ...prev,
        body: editorRef.current.innerHTML
      }));
    }
  };

  const insertImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
      handleEditorInput();
    }
  };

  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
      handleEditorInput();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-templates-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-['Manrope']">Email Templates</h1>
        <p className="text-muted-foreground mt-1">
          Customize system emails sent to customers. Use variables like {'{{first_name}}'} for personalization.
        </p>
      </div>

      {/* Info Banner */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Email Sending is Currently Simulated</p>
              <p className="text-sm text-amber-700">
                These templates define email content. Emails are logged to contact timelines but not actually sent yet. 
                Real sending will be enabled with IMAP/SMTP integration before launch.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((template) => {
          const info = templateTypeInfo[template.template_type] || templateTypeInfo.custom;
          const Icon = info.icon;
          
          return (
            <Card 
              key={template.id} 
              className={`hover:shadow-md transition-shadow ${!template.is_active ? 'opacity-60' : ''}`}
              data-testid={`template-card-${template.template_type}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${info.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <CardDescription className="text-xs">{info.desc}</CardDescription>
                    </div>
                  </div>
                  {!template.is_active && (
                    <Badge variant="outline" className="text-xs">Inactive</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Subject:</p>
                  <p className="text-sm font-medium truncate">{template.subject}</p>
                </div>
                
                {/* Show timer info for reminder templates */}
                {info.hasTimer && template.send_before_minutes && (
                  <div className="flex items-center gap-2 text-sm text-orange-600">
                    <Clock className="h-4 w-4" />
                    <span>Sent {template.send_before_minutes} min before meeting</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={() => openEditDialog(template)}
                    data-testid={`edit-template-${template.template_type}`}
                  >
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handlePreview(template)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleReset(template)}
                    title="Reset to default"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Edit Email Template
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate && templateTypeInfo[selectedTemplate.template_type]?.desc}
            </DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="flex-1 overflow-y-auto space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                    data-testid="template-name-input"
                  />
                </div>
                <div className="space-y-2 flex items-end gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={editForm.is_active}
                      onCheckedChange={(v) => setEditForm(p => ({ ...p, is_active: v }))}
                    />
                    <Label>Active</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Subject Line</Label>
                <Input
                  value={editForm.subject}
                  onChange={(e) => setEditForm(p => ({ ...p, subject: e.target.value }))}
                  placeholder="Email subject with {{variables}}"
                  data-testid="template-subject-input"
                />
              </div>

              {/* Timer setting for reminder emails */}
              {templateTypeInfo[selectedTemplate.template_type]?.hasTimer && (
                <div className="space-y-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-600" />
                    <Label className="text-orange-800 font-medium">Reminder Timing</Label>
                  </div>
                  <p className="text-sm text-orange-700 mb-2">
                    Set how long before the meeting this reminder should be sent.
                  </p>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min="5"
                      max="1440"
                      value={editForm.send_before_minutes || 60}
                      onChange={(e) => setEditForm(p => ({ ...p, send_before_minutes: parseInt(e.target.value) || 60 }))}
                      className="w-24"
                      data-testid="send-before-minutes-input"
                    />
                    <span className="text-sm text-orange-700">minutes before meeting</span>
                  </div>
                  <p className="text-xs text-orange-600 mt-2">
                    Common times: 60 min (1 hour), 1440 min (24 hours), 2880 min (48 hours)
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Email Body</Label>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setUseRichEditor(!useRichEditor)}
                      className="text-xs"
                    >
                      <Code className="h-3 w-3 mr-1" />
                      {useRichEditor ? 'Plain Text' : 'Rich Editor'}
                    </Button>
                  </div>
                </div>
                
                {useRichEditor ? (
                  <div className="border rounded-lg overflow-hidden" data-testid="template-body-rich-editor">
                    {/* Rich Text Toolbar */}
                    <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { execCommand('bold'); handleEditorInput(); }} title="Bold">
                        <Bold className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { execCommand('italic'); handleEditorInput(); }} title="Italic">
                        <Italic className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { execCommand('underline'); handleEditorInput(); }} title="Underline">
                        <Underline className="h-4 w-4" />
                      </Button>
                      <div className="w-px h-6 bg-slate-200 mx-1" />
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { execCommand('insertUnorderedList'); handleEditorInput(); }} title="Bullet List">
                        <List className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { execCommand('justifyLeft'); handleEditorInput(); }} title="Align Left">
                        <AlignLeft className="h-4 w-4" />
                      </Button>
                      <div className="w-px h-6 bg-slate-200 mx-1" />
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={insertLink} title="Insert Link">
                        <LinkIcon className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={insertImage} title="Insert Image">
                        <Image className="h-4 w-4" />
                      </Button>
                      <div className="w-px h-6 bg-slate-200 mx-1" />
                      <select 
                        className="h-8 px-2 text-sm border rounded"
                        onChange={(e) => { execCommand('formatBlock', e.target.value); handleEditorInput(); }}
                        defaultValue=""
                      >
                        <option value="" disabled>Format</option>
                        <option value="p">Paragraph</option>
                        <option value="h1">Heading 1</option>
                        <option value="h2">Heading 2</option>
                        <option value="h3">Heading 3</option>
                      </select>
                    </div>
                    {/* Editable Content Area */}
                    <div
                      ref={editorRef}
                      contentEditable
                      onInput={handleEditorInput}
                      dangerouslySetInnerHTML={{ __html: editForm.body }}
                      className="min-h-[250px] p-4 focus:outline-none text-sm"
                      style={{ lineHeight: '1.6' }}
                      data-testid="template-body-editor"
                    />
                  </div>
                ) : (
                  <Textarea
                    value={editForm.body}
                    onChange={(e) => setEditForm(p => ({ ...p, body: e.target.value }))}
                    placeholder="Write your email content..."
                    rows={12}
                    className="font-mono text-sm"
                    data-testid="template-body-input"
                  />
                )}
                
                <p className="text-xs text-muted-foreground">
                  {useRichEditor 
                    ? 'Use the toolbar to add images, formatting, links, and more. Perfect for signature cards!' 
                    : 'Plain text mode. Use {{variable}} syntax for dynamic content.'}
                </p>
              </div>

              {/* Available Variables */}
              {selectedTemplate.available_variables && selectedTemplate.available_variables.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-2">Available Variables (click to insert):</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.available_variables.map(variable => (
                      <Button
                        key={variable}
                        variant="outline"
                        size="sm"
                        className="text-xs h-7 bg-white hover:bg-blue-100"
                        onClick={() => insertVariable(variable)}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        {`{{${variable}}}`}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-template-btn">
              <Save className="h-4 w-4 mr-1" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Email Preview
            </DialogTitle>
            <DialogDescription>
              Preview with sample data. Actual emails will use real customer information.
            </DialogDescription>
          </DialogHeader>
          
          {previewContent && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-100 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Subject:</p>
                <p className="font-medium">{previewContent.subject}</p>
              </div>
              
              <div className="border rounded-lg">
                <div className="p-4 bg-white">
                  <ScrollArea className="h-[300px]">
                    <div className="whitespace-pre-wrap text-sm">
                      {previewContent.body}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;
